import { useRef, useCallback, useEffect, useMemo } from "react"
import { motion } from "motion/react"

interface BetControlsProps {
  value: number
  onChange: (v: number) => void
  min: number
  max: number
  step: number
  onRaise: () => void
  isMobile?: boolean
  currentBet?: number
  pot?: number
}

const BetControls = ({ value, onChange, min, max, step, onRaise, isMobile, currentBet = 0, pot = 0 }: BetControlsProps) => {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const valueRef = useRef(value)
  valueRef.current = value

  const clamp = useCallback((v: number) => Math.max(min, Math.min(max, v)), [min, max])

  const stopHold = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    intervalRef.current = null
    timeoutRef.current = null
  }, [])

  useEffect(() => () => stopHold(), [stopHold])

  const startHold = useCallback((direction: 1 | -1) => {
    stopHold()
    let tick = 0
    timeoutRef.current = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        tick++
        const accel = tick > 20 ? step * 10 : tick > 10 ? step * 5 : tick > 5 ? step * 2 : step
        const next = clamp(valueRef.current + direction * accel)
        onChange(next)
      }, 80)
    }, 400)
  }, [step, clamp, onChange, stopHold])

  const handleTap = useCallback((direction: 1 | -1) => {
    onChange(clamp(value + direction * step))
  }, [value, step, clamp, onChange])

  const quickBets = useMemo(() => [
    { label: "Min", target: min },
    { label: "2\u00d7", target: Math.max(min, currentBet * 2) },
    { label: "3\u00d7", target: Math.max(min, currentBet * 3) },
    { label: "Pot", target: Math.max(min, pot) },
    { label: "Max", target: max },
  ], [min, max, currentBet, pot])

  const btnSize = isMobile ? "w-11 h-11" : "w-10 h-10"
  const fontStyle = { fontFamily: "Outfit, -apple-system, BlinkMacSystemFont, sans-serif" }

  return (
    <div className="flex flex-col gap-1.5 flex-1">
      {/* Quick-bet buttons */}
      <div className="flex gap-1">
        {quickBets.map((b) => {
          const clamped = Math.min(b.target, max)
          const isSelected = value === clamped && clamped >= min
          const isDisabled = b.target > max || b.target < min
          return (
            <motion.button
              key={b.label}
              onClick={() => onChange(clamped)}
              disabled={isDisabled}
              className={`flex-1 py-1 text-xs font-bold rounded-lg border select-none transition-colors ${
                isSelected
                  ? "bg-[#F37020] border-[#F37020] text-white shadow-[0_0_8px_rgba(243,112,32,0.4)]"
                  : "bg-white/5 border-white/15 text-white/70 hover:bg-white/10 hover:text-white"
              } disabled:opacity-30 disabled:cursor-not-allowed`}
              style={fontStyle}
              whileTap={isDisabled ? undefined : { scale: 0.92 }}
            >
              {b.label}
            </motion.button>
          )
        })}
      </div>

      {/* +/- controls + Raise */}
      <div className="flex items-center gap-1.5">
        <motion.button
          className={`${btnSize} flex items-center justify-center bg-[#1C1C32] text-white font-bold text-lg rounded-xl border border-white/20 select-none touch-none disabled:opacity-40`}
          style={fontStyle}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => handleTap(-1)}
          onPointerDown={() => startHold(-1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          disabled={value <= min}
        >
          −
        </motion.button>

        <div
          className="flex-1 px-2 py-1.5 bg-[#1C1C32] text-white text-sm font-bold rounded-lg border border-white/20 text-center tabular-nums min-w-[60px]"
          style={fontStyle}
        >
          {value.toLocaleString()}
        </div>

        <motion.button
          className={`${btnSize} flex items-center justify-center bg-[#1C1C32] text-white font-bold text-lg rounded-xl border border-white/20 select-none touch-none disabled:opacity-40`}
          style={fontStyle}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.9 }}
          onClick={() => handleTap(1)}
          onPointerDown={() => startHold(1)}
          onPointerUp={stopHold}
          onPointerLeave={stopHold}
          onPointerCancel={stopHold}
          disabled={value >= max}
        >
          +
        </motion.button>

        <motion.button
          onClick={onRaise}
          className="px-4 py-2.5 bg-[#F37020] text-white font-bold text-sm border-2 border-[#2A2A45] rounded-xl shadow-[0_0_6px_rgba(243,112,32,0.1)]"
          style={fontStyle}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Raise
        </motion.button>
      </div>
    </div>
  )
}

export default BetControls
